import CommentIcon from "@mui/icons-material/Comment";
import DescriptionIcon from "@mui/icons-material/Description";
import {
  CloudUpload as CloudUploadIcon,
  InsertDriveFile as FileIcon,
} from "@mui/icons-material";
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import api from "../services/api";
import documentoService from "../services/documento";
import protocoloService from "../services/protocolo";
import usuarioService from "../services/usuario";
import {
  GRUPO_ATIVIDADE_FLORESTAL,
  getAtividadeFlorestalResumo,
  PROTOCOLO_STATUS_CRIADO,
  PROTOCOLO_STATUS_EM_ANDAMENTO,
  PROTOCOLO_STATUS_EMITIDO,
  PROTOCOLO_STATUS_AGUARDANDO_APROVACAO,
  PROTOCOLO_STATUS_APROVADA,
  protocoloStatusLabels,
} from "../utils/protocoloConstants";
import { getMuiChipColor, getProtocoloPaletteKey } from "../utils/statusPalette";
import { formatCpfCnpj, formatDateToShow, formatPhone } from "../utils/formatters";
import { baixarRequerimentoProtocoloPdf } from "../utils/requerimentoProtocoloPdf";
import {
  abrirDocumentoArquivo,
  documentoEstaEnviado,
} from "../utils/abrirDocumentoArquivo";
import { getApiErrorMessage } from "../utils/getApiErrorMessage";
import AdicionarDocumentoModal from "./AdicionarDocumentoModal";
import AdicionarCondicionanteModal from "./AdicionarCondicionanteModal";
import ComentariosDeUmDocumento from "./ComentariosDeUmDocumento";

const PROTOCOLO_STATUS_OPTIONS = [
  { value: 0, label: "Criado" },
  { value: 1, label: "Em Andamento" },
  { value: 3, label: "Aguardando Aprovação" },
];

function abrirArquivoBase64(conteudo, contextType) {
  const mimeType = contextType || "application/pdf";
  const byteCharacters = atob(conteudo);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: mimeType });
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank");
}

function ProtocoloAnalistaModal({ open, onClose, protocolo, onAtualizado }) {
  const [detalhes, setDetalhes] = useState(null);
  const [requerente, setRequerente] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [documentos, setDocumentos] = useState([]);
  const [condicionantes, setCondicionantes] = useState([]);
  const [loadingDocumentos, setLoadingDocumentos] = useState(false);
  const [novoStatus, setNovoStatus] = useState("");
  const [observacoes, setObservacoes] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [adicionarDocumentoOpen, setAdicionarDocumentoOpen] = useState(false);
  const [adicionarCondicionanteOpen, setAdicionarCondicionanteOpen] = useState(false);
  const [documentoSelecionado, setDocumentoSelecionado] = useState(null);
  const [comentarioDialogOpen, setComentarioDialogOpen] = useState(false);
  const [condicionanteSelecionado, setCondicionanteSelecionado] = useState(null);
  const [condicionanteComentarioOpen, setCondicionanteComentarioOpen] = useState(false);

  const protocoloId = protocolo?.id;

  const carregarDocumentos = useCallback(async () => {
    if (!protocoloId) return;
    try {
      const response = await documentoService.obterDocumentosPorProtocolo(protocoloId);
      setDocumentos(response?.data ?? []);
    } catch {
      setDocumentos([]);
    }
  }, [protocoloId]);

  const carregarCondicionantes = useCallback(async () => {
    if (!protocoloId) return;
    try {
      const response = await api.get(`Condicionante/protocolo/${protocoloId}`);
      setCondicionantes(response.data?.data ?? []);
    } catch {
      setCondicionantes([]);
    }
  }, [protocoloId]);

  const carregarDetalhes = useCallback(async (options = {}) => {
    const { manageLoading = true } = options;
    if (!protocoloId) return;
    if (manageLoading) setLoading(true);
    try {
      const data = await protocoloService.obterPorId(protocoloId);
      setDetalhes(data);
      setNovoStatus(data?.status ?? "");
      setObservacoes(data?.observacoesAnalista ?? "");

      if (data?.createUserId) {
        try {
          const usuario = await usuarioService.obterUsuarioPorId(data.createUserId);
          setRequerente(usuario);
        } catch {
          setRequerente(null);
        }
      }
    } catch {
      setDetalhes(null);
    } finally {
      if (manageLoading) setLoading(false);
    }
  }, [protocoloId]);

  useEffect(() => {
    if (!open || !protocoloId) {
      setDetalhes(null);
      setRequerente(null);
      setDocumentos([]);
      setCondicionantes([]);
      return;
    }

    setLoading(true);
    setLoadingDocumentos(true);
    Promise.all([
      carregarDetalhes({ manageLoading: false }),
      carregarDocumentos(),
      carregarCondicionantes(),
    ]).finally(() => {
      setLoading(false);
      setLoadingDocumentos(false);
    });
  }, [open, protocoloId, carregarDetalhes, carregarDocumentos, carregarCondicionantes]);

  const nomeRequerente = requerente
    ? `${requerente.nome || requerente.first_name || ""} ${requerente.sobrenome || requerente.last_name || ""}`.trim()
    : "—";

  const handleBaixarRequerimento = async () => {
    if (!detalhes || detalhes.status !== PROTOCOLO_STATUS_EMITIDO) return;
    setDownloadingPdf(true);
    try {
      await baixarRequerimentoProtocoloPdf(detalhes, requerente);
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleEmitir = async () => {
    if (!detalhes?.id || detalhes.status !== PROTOCOLO_STATUS_APROVADA) return;

    setSalvando(true);
    try {
      await protocoloService.emitir(detalhes.id, observacoes || null);
      alert("Protocolo emitido com sucesso!");
      await carregarDetalhes();
      onAtualizado?.();
    } catch (error) {
      alert(
        error?.response?.data?.message ||
          "Erro ao emitir protocolo. Tente novamente.",
      );
    } finally {
      setSalvando(false);
    }
  };

  const handleSalvarAnalise = async () => {
    if (!detalhes?.id) return;
    if (novoStatus === "" || novoStatus === null || novoStatus === undefined) {
      alert("Por favor, selecione um status.");
      return;
    }

    setSalvando(true);
    try {
      await protocoloService.atualizarAnalista(
        detalhes.id,
        Number(novoStatus),
        observacoes || null,
      );
      alert("Protocolo atualizado com sucesso!");
      await carregarDetalhes();
      onAtualizado?.();
      onClose();
    } catch (error) {
      alert(
        error?.response?.data?.message ||
          "Erro ao atualizar protocolo. Tente novamente.",
      );
    } finally {
      setSalvando(false);
    }
  };

  const processoSelecionado = detalhes ? { id: detalhes.id } : null;
  const emitido = detalhes?.status === PROTOCOLO_STATUS_EMITIDO;
  const aprovada = detalhes?.status === PROTOCOLO_STATUS_APROVADA;
  const bloqueadoParaAnalise =
    emitido || detalhes?.status === PROTOCOLO_STATUS_AGUARDANDO_APROVACAO;
  const permiteAdicionarDocumentoCondicionante =
    detalhes?.status === PROTOCOLO_STATUS_CRIADO ||
    detalhes?.status === PROTOCOLO_STATUS_EM_ANDAMENTO;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Análise do Protocolo — {detalhes?.numeroProcesso || protocolo?.numeroProcesso || ""}
        </DialogTitle>
        <Divider />
        <DialogContent
          sx={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto", position: "relative" }}
        >
          {loading && !detalhes ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 6,
                gap: 2,
              }}
            >
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                Carregando análise do protocolo...
              </Typography>
            </Box>
          ) : !detalhes ? (
            <Typography color="text.secondary">
              Não foi possível carregar os detalhes.
            </Typography>
          ) : (
            <>
              <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: "background.default", borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Informações do Processo
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">Nº processo</Typography>
                    <Typography variant="body1">{detalhes.numeroProcesso || "—"}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">Tipo de processo</Typography>
                    <Typography variant="body1">{detalhes.processoNome || "—"}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">Grupo</Typography>
                    <Typography variant="body1">{detalhes.grupoDeAtividade || GRUPO_ATIVIDADE_FLORESTAL}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">Atividade</Typography>
                    <Typography variant="body1">
                      {getAtividadeFlorestalResumo(detalhes.atividadeEmpreendimento)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Especificação</Typography>
                    <Typography variant="body1">{detalhes.especificacao || "—"}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                    <Chip
                      label={protocoloStatusLabels[detalhes.status] ?? detalhes.status}
                      size="small"
                      color={getMuiChipColor(getProtocoloPaletteKey(detalhes.status))}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">Data</Typography>
                    <Typography variant="body1">{formatDateToShow(detalhes.createAt)}</Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Informações do Requerente
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Requerente:</strong> {nomeRequerente}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>CPF/CNPJ:</strong> {requerente?.cpf ? formatCpfCnpj(requerente.cpf) : "—"}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Contato:</strong> {requerente?.telefone ? formatPhone(requerente.telefone) : "—"}
                </Typography>
                <Typography variant="body2">
                  <strong>Endereço:</strong> {detalhes.empreendimentoEnderecoFormatado || "—"}
                </Typography>
              </Paper>

              <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="h6">Documentos do Protocolo</Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => setAdicionarDocumentoOpen(true)}
                    disabled={!permiteAdicionarDocumentoCondicionante}
                  >
                    Adicionar Documento
                  </Button>
                </Box>
                {loadingDocumentos ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : documentos.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Nenhum documento anexado.
                  </Typography>
                ) : (
                  <List dense>
                    {documentos.map((doc) => {
                      const docId = doc.data || doc.id;
                      const enviado = documentoEstaEnviado(doc.status);
                      return (
                        <ListItem
                          key={docId}
                          sx={{
                            borderLeft: "4px solid",
                            borderLeftColor: enviado ? "success.main" : "warning.main",
                            mb: 1,
                            borderRadius: 1,
                          }}
                        >
                          <ListItemIcon>
                            <FileIcon
                              role="img"
                              aria-label={`Documento, ${enviado ? "enviado" : "pendente"}`}
                              color={enviado ? "success" : "warning"}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={doc.fileName}
                            secondary={doc.descricao || (enviado ? "Enviado" : "Pendente de envio")}
                          />
                          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                            {enviado && (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={async () => {
                                  try {
                                    await abrirDocumentoArquivo(docId);
                                  } catch (error) {
                                    alert(
                                      getApiErrorMessage(
                                        error,
                                        "Não foi possível abrir o documento.",
                                      ),
                                    );
                                  }
                                }}
                              >
                                Visualizar
                              </Button>
                            )}
                            {doc.hasMessages && (
                              <Tooltip title="Ver comentários">
                                <IconButton
                                  size="small"
                                  aria-label="Ver comentários"
                                  onClick={() => {
                                    setDocumentoSelecionado({ ...doc, id: docId, nome: doc.fileName });
                                    setComentarioDialogOpen(true);
                                  }}
                                >
                                  <Badge badgeContent="!" color="error">
                                    <CommentIcon fontSize="small" />
                                  </Badge>
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </Paper>

              <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="h6">Condicionantes</Typography>
                  <Button
                    variant="contained"
                    color="secondary"
                    size="small"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => setAdicionarCondicionanteOpen(true)}
                    disabled={!permiteAdicionarDocumentoCondicionante}
                  >
                    Adicionar Condicionante
                  </Button>
                </Box>
                {condicionantes.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Nenhum condicionante cadastrado.
                  </Typography>
                ) : (
                  <List dense>
                    {condicionantes.map((cond) => {
                      const pendente = cond.status === 1;
                      return (
                        <ListItem
                          key={cond.id}
                          sx={{
                            borderLeft: "4px solid",
                            borderLeftColor: pendente ? "warning.main" : "success.main",
                            mb: 1,
                            borderRadius: 1,
                          }}
                        >
                          <ListItemIcon>
                            <Badge color={pendente ? "warning" : "success"} variant="dot">
                              <FileIcon
                                role="img"
                                aria-label={`Condicionante, ${pendente ? "pendente" : "atendido"}`}
                              />
                            </Badge>
                          </ListItemIcon>
                          <ListItemText
                            primary={cond.fileName}
                            secondary={pendente ? "Pendente" : "Atendido"}
                          />
                          <Box sx={{ display: "flex", gap: 1 }}>
                            {!pendente && cond.conteudo && (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => abrirArquivoBase64(cond.conteudo, cond.contextType)}
                              >
                                Visualizar
                              </Button>
                            )}
                            <Tooltip title={cond.hasMessages ? "Ver comentários" : "Adicionar comentário"}>
                              <IconButton
                                size="small"
                                aria-label={cond.hasMessages ? "Ver comentários" : "Adicionar comentário"}
                                onClick={() => {
                                  setCondicionanteSelecionado(cond);
                                  setCondicionanteComentarioOpen(true);
                                }}
                              >
                                <Badge badgeContent={cond.hasMessages ? "!" : 0} color="error">
                                  <CommentIcon fontSize="small" />
                                </Badge>
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </Paper>

              <Paper sx={{ p: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  Análise
                </Typography>
                {aprovada && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Protocolo aprovado pelo secretário. Use &quot;Emitir Protocolo&quot; para
                    liberar o download do requerimento.
                  </Alert>
                )}
                {aprovada ? (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Status
                    </Typography>
                    <Chip
                      label={protocoloStatusLabels[PROTOCOLO_STATUS_APROVADA]}
                      size="small"
                      color={getMuiChipColor(getProtocoloPaletteKey(PROTOCOLO_STATUS_APROVADA))}
                    />
                  </Box>
                ) : (
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={novoStatus}
                      onChange={(e) => setNovoStatus(e.target.value)}
                      label="Status"
                      disabled={bloqueadoParaAnalise}
                    >
                      {PROTOCOLO_STATUS_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Observações"
                  value={observacoes}
                  onChange={(e) => setObservacoes(e.target.value)}
                  disabled={bloqueadoParaAnalise && !aprovada}
                  inputProps={{ maxLength: 2000 }}
                />
              </Paper>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {emitido && (
            <Button
              onClick={handleBaixarRequerimento}
              disabled={downloadingPdf || loading}
              startIcon={<DescriptionIcon />}
            >
              {downloadingPdf ? "Baixando..." : "Baixar REQUERIMENTO"}
            </Button>
          )}
          <Button onClick={onClose}>{emitido || aprovada ? "Fechar" : "Cancelar"}</Button>
          {aprovada && (
            <Button
              variant="contained"
              color="success"
              onClick={handleEmitir}
              disabled={salvando || loading}
            >
              {salvando ? "Emitindo..." : "Emitir Protocolo"}
            </Button>
          )}
          {!bloqueadoParaAnalise && !aprovada && (
            <Button
              variant="contained"
              onClick={handleSalvarAnalise}
              disabled={salvando || loading}
            >
              {salvando ? "Salvando..." : "Salvar Análise"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <AdicionarDocumentoModal
        open={adicionarDocumentoOpen}
        onClose={() => setAdicionarDocumentoOpen(false)}
        onDocumentoAdicionado={(docs) => setDocumentos(docs)}
        processoSelecionado={processoSelecionado}
        tipo="protocolo"
      />

      <AdicionarCondicionanteModal
        open={adicionarCondicionanteOpen}
        onClose={() => setAdicionarCondicionanteOpen(false)}
        processoSelecionado={processoSelecionado}
        tipo="protocolo"
        onCondicionanteAdicionado={carregarCondicionantes}
      />

      <ComentariosDeUmDocumento
        open={comentarioDialogOpen}
        onClose={() => setComentarioDialogOpen(false)}
        documento={documentoSelecionado}
        processo={processoSelecionado}
        onComentarioSalvo={carregarDocumentos}
      />

      <ComentariosDeUmDocumento
        open={condicionanteComentarioOpen}
        onClose={() => setCondicionanteComentarioOpen(false)}
        documento={condicionanteSelecionado}
        processo={processoSelecionado}
        onComentarioSalvo={carregarCondicionantes}
        tipoRecurso="condicionantes"
      />
    </>
  );
}

export default ProtocoloAnalistaModal;
